// src/pages/Statements/StatementDetails.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Toast from '../../components/Toast/Toast';
import Pagination from './Pagination';
import './Statements.css';
import './StatementDetails.css';
import TourMap from '../../components/TourMap/TourMap';

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];

const PAGE_SIZE = 20;

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).replace(/ /g, '-');
};

const fmtNum = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  const num = Number(n);
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-IN');
};

// NOTE: subDealerVisits is not yet a backend field (see Statement/tour_entries entity).
// It defaults to 0 here as a placeholder until the API returns it.
// Farmer Visit = firstVisits + followupVisits (both are farmer-side visits today).
const deriveVisits = (t) => {
  const farmerVisits = (Number(t.firstVisits) || 0) + (Number(t.followupVisits) || 0);
  const subDealerVisits = Number(t.subDealerVisits) || 0; // placeholder until backend adds this
  const dealerVisits = Number(t.dealerVisits) || 0;
  const totalVisits = farmerVisits + subDealerVisits + dealerVisits;
  return { farmerVisits, subDealerVisits, dealerVisits, totalVisits };
};

const StatementDetails = () => {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const month = parseInt(searchParams.get('month'), 10);
  const year  = parseInt(searchParams.get('year'), 10);
  const type  = searchParams.get('type') || 'REGULAR';

  const employeeName = location.state?.employeeName || 'Employee';
  const monthLabel   = location.state?.monthLabel   ||
    (month && year ? `${MONTH_NAMES[month - 1]}-${String(year).slice(-2)}` : '—');

  const [tours,   setTours]   = useState([]);
  const [mapModal, setMapModal] = useState({ open: false, loading: false, tour: null, data: null });

  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);
  const [toast,   setToast]   = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/tours/employee/${userId}/monthly-tours?month=${month}&year=${year}`)
      .then(res => {
        const data = res.data.data || [];
        setTours(data);
      })
      .catch(err => {
        setToast({
          message: err.response?.data?.message || 'Failed to load statement',
          type: 'error',
        });
      })
      .finally(() => setLoading(false));
  }, [userId, month, year]);

const openTourMap = async (tourRow) => {
  setMapModal({ open: true, loading: true, tour: tourRow, data: null });
  try {
    const res = await api.get(`/tours/${tourRow.id}/map-data`);
    setMapModal(prev => ({ ...prev, loading: false, data: res.data.data }));
  } catch (err) {
    setMapModal(prev => ({ ...prev, loading: false, data: null }));
    setToast({ message: err.response?.data?.message || 'Failed to load tour map', type: 'error' });
  }
};

const closeTourMap = () => setMapModal({ open: false, loading: false, tour: null, data: null });   // ✅ ADD THIS

  // Totals across full month
  const totals = useMemo(() => {
    return tours.reduce((acc, t) => {
      const v = deriveVisits(t);
      return {
        startKm:         acc.startKm         + (Number(t.startKm) || 0),
        endKm:           acc.endKm           + (Number(t.endKm)   || 0),
        totalKm:         acc.totalKm         + (Number(t.totalKm) || 0),
        farmerVisits:    acc.farmerVisits    + v.farmerVisits,
        subDealerVisits: acc.subDealerVisits + v.subDealerVisits,
        dealerVisits:    acc.dealerVisits    + v.dealerVisits,
        totalVisits:     acc.totalVisits     + v.totalVisits,
        workingDays:     acc.workingDays     + (t.tourType === 'WORKING' || t.tourType === 'CAMPAIGNING' ? 1 : 0),
      };
    }, {
      startKm: 0, endKm: 0, totalKm: 0,
      farmerVisits: 0, subDealerVisits: 0, dealerVisits: 0, totalVisits: 0,
      workingDays: 0,
    });
  }, [tours]);

  const totalPages = Math.max(1, Math.ceil(tours.length / PAGE_SIZE));
  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return tours.slice(start, start + PAGE_SIZE);
  }, [tours, page]);

  const handleExport = () => {
    if (tours.length === 0) {
      setToast({ message: 'Nothing to export', type: 'error' });
      return;
    }

    const headers = ['No', 'Date', 'Tour Type', 'Destination', 'Mode', 'Start KM', 'End KM', 'Total KM', 'Farmer Visit', 'Sub-Dealer Visit', 'Dealer Visit', 'Total Visits'];
    const rows = tours.map((t, i) => {
      const v = deriveVisits(t);
      return [
        i + 1,
        fmtDate(t.tourDate),
        t.tourType || '—',
        t.startDestination || '—',
        t.modeOfTransport || '—',
        t.startKm ?? '',
        t.endKm ?? '',
        t.totalKm ?? '',
        v.farmerVisits,
        v.subDealerVisits,
        v.dealerVisits,
        v.totalVisits,
      ];
    });

    const totalsRow = [
      '', '', '', '', 'Total:',
      totals.startKm, totals.endKm, totals.totalKm,
      totals.farmerVisits, totals.subDealerVisits, totals.dealerVisits, totals.totalVisits,
    ];

    const csv = [headers, ...rows, totalsRow]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `TourStatement_${employeeName.replace(/\s+/g, '_')}_${monthLabel}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setToast({ message: '✅ Statement exported', type: 'success' });
  };

  return (
    <div className="stmt-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav aria-label="breadcrumb" className="stmt-breadcrumb">
        <span onClick={() => navigate('/statements/employees')} style={{ cursor: 'pointer' }}>Statements</span>
        <span>›</span>
        <span onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>{employeeName}</span>
        <span>›</span>
        <span className="active">{monthLabel}</span>
      </nav>

      <div className="stmt-profile-header">
        <button className="stmt-back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div className="stmt-profile-title-wrap">
          <div className="stmt-profile-avatar">
            {(employeeName[0] || '?').toUpperCase()}
          </div>
          <div>
            <h4 className="stmt-profile-name">{employeeName}</h4>
            <p className="stmt-profile-sub">
              Tour Statement — {monthLabel} · {type === 'REGULAR' ? 'Regular View' : 'FV Based View'}
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="stmt-card">
          <div className="stmt-loading">Loading tour statement...</div>
        </div>
      ) : (
        <>
   
          {/* Summary card */}
          <div className="sd-info-card">
            <div className="sd-info-grid">
              <div className="sd-info-item">
                <span className="sd-info-label">Total Days</span>
                <span className="sd-info-value">{tours.length}</span>
              </div>
              <div className="sd-info-item">
                <span className="sd-info-label">Working Days</span>
                <span className="sd-info-value">{totals.workingDays}</span>
              </div>
              <div className="sd-info-item">
                <span className="sd-info-label">Total KM</span>
                <span className="sd-info-value" style={{ color: '#1565c0' }}>
                  {fmtNum(totals.totalKm)}
                </span>
              </div>
              <div className="sd-info-item">
                <span className="sd-info-label">Total Visits</span>
                <span className="sd-info-value" style={{ color: '#2e7d32' }}>
                  {totals.totalVisits}
                </span>
              </div>
            </div>
          </div>

          <div className="stmt-card">
            {tours.length === 0 ? (
              <div className="stmt-empty">
                <div className="stmt-empty-icon">📅</div>
                <p className="stmt-empty-title">No tours in this month</p>
                <p className="stmt-empty-sub">
                  {employeeName} didn't log any tours in {monthLabel}
                </p>
              </div>
            ) : (
              <>
                <div className="stmt-table-wrap">
                  <table className="stmt-table sd-table">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>No</th>
                        <th style={{ width: 130 }}>Date</th>
                        <th style={{ width: 110 }}>Type</th>
                        <th>Destination</th>
                        <th style={{ width: 110 }} className="sd-num">Start KM</th>
                        <th style={{ width: 110 }} className="sd-num">End KM</th>
                        <th style={{ width: 110 }} className="sd-num">Total KM</th>
                        <th style={{ width: 100 }} className="sd-num">Farmer Visit</th>
                        <th style={{ width: 130 }} className="sd-num">Sub-Dealer Visit</th>
                        <th style={{ width: 100 }} className="sd-num">Dealer Visit</th>
                        <th style={{ width: 110 }} className="sd-num">Total Visits</th>
                        <th style={{ width: 110 }}>Map</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((t, idx) => {
                        const v = deriveVisits(t);
                        return (
                          <tr key={t.id}>
                            <td className="stmt-td-num">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                            <td className="sd-date">{fmtDate(t.tourDate)}</td>
                            <td>
                              <span className={`sd-tour-badge sd-tour-${(t.tourType || '').toLowerCase()}`}>
                                {t.tourType || '—'}
                              </span>
                            </td>
                            <td className="sd-destination">{t.startDestination || '—'}</td>
                            <td className="sd-num sd-num-value">{fmtNum(t.startKm)}</td>
                            <td className="sd-num sd-num-value">{fmtNum(t.endKm)}</td>
                            <td className="sd-num sd-num-value sd-total-km">{fmtNum(t.totalKm)}</td>
                            <td className="sd-num sd-num-value" style={{ color: '#2e7d32' }}>
                              {v.farmerVisits}
                            </td>
                            <td className="sd-num sd-num-value" style={{ color: '#6a1b9a' }}>
                              {v.subDealerVisits}
                            </td>
                            <td className="sd-num sd-num-value" style={{ color: '#f57c00' }}>
                              {v.dealerVisits}
                            </td>
                            <td className="sd-num sd-num-value" style={{ color: '#1976d2' }}>
                              {v.totalVisits}
                            </td>
                            <td>
  <button className="sd-view-map-btn" onClick={() => openTourMap(t)}>
    🗺️ View Map
  </button>
</td>
                          </tr>
                        );
                      })}

                      {/* Totals row */}
                      <tr className="sd-totals-row">
                        <td colSpan={4} className="sd-totals-label">
                          Total ({tours.length} tour{tours.length === 1 ? '' : 's'}):
                        </td>
                        <td className="sd-num">{fmtNum(totals.startKm)}</td>
                        <td className="sd-num">{fmtNum(totals.endKm)}</td>
                        <td className="sd-num sd-total-km">{fmtNum(totals.totalKm)}</td>
                        <td className="sd-num" style={{ color: '#2e7d32' }}>{totals.farmerVisits}</td>
                        <td className="sd-num" style={{ color: '#6a1b9a' }}>{totals.subDealerVisits}</td>
                        <td className="sd-num" style={{ color: '#f57c00' }}>{totals.dealerVisits}</td>
                        <td className="sd-num" style={{ color: '#1976d2' }}>{totals.totalVisits}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={tours.length}
                  pageSize={PAGE_SIZE}
                  onChange={setPage}
                />

                <div className="sd-export-wrap">
                  <button className="sd-export-btn" onClick={handleExport}>
                    📊 Export to Excel (all {tours.length} rows)
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
      {mapModal.open && (
  <div className="stmt-map-overlay" onClick={closeTourMap}>
    <div className="stmt-map-modal" onClick={(e) => e.stopPropagation()}>
      <div className="stmt-map-modal-header">
        <div>
          <h5 className="stmt-map-modal-title">
            Tour Map — {fmtDate(mapModal.tour?.tourDate)}
          </h5>
          <p className="stmt-map-modal-sub">
            {mapModal.tour?.startDestination || '—'}
            {mapModal.data ? ` · ${mapModal.data.visits.length} visit${mapModal.data.visits.length === 1 ? '' : 's'}` : ''}
          </p>
        </div>
        <button className="stmt-map-close-btn" onClick={closeTourMap}>✕</button>
      </div>
      <div className="stmt-map-modal-body">
        {mapModal.loading ? (
          <div className="stmt-loading">Loading tour map...</div>
        ) : mapModal.data ? (
          <TourMap tour={mapModal.data.tour} visits={mapModal.data.visits} />
        ) : (
          <div className="stmt-empty">
            <p className="stmt-empty-title">Couldn't load map data</p>
          </div>
        )}
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default StatementDetails;